import { useCallback, useEffect, useState } from "react";
import { BellRing, CheckCircle2, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtDT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR")} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

export function NotifyClientButton({ clientId, pendingCount = null }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/validation-reminder`).then((r) => {
      setData(r.data);
      setLast(r.data.last_reminder || null);
    }).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load, pendingCount]);

  const openModal = () => {
    setSubject(data?.subject || "Publications en attente de validation");
    setBody(data?.body || "");
    setOpen(true);
    load();
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/validation-reminder`, { subject, body });
      toast.success(`Relance envoyée à ${r.data.recipient}`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setBusy(false); }
  };

  const count = pendingCount ?? data?.pending_count ?? 0;

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap" data-testid="notify-client-wrap">
        {last && (
          <span className="text-xs text-green-700 inline-flex items-center gap-1.5" data-testid="notify-client-last">
            <CheckCircle2 className="w-3.5 h-3.5" /> Client notifié le {fmtDT(last.sent_at)}
          </span>
        )}
        <Button size="sm" variant="outline" onClick={openModal}
          className="gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-50"
          data-testid="notify-client-btn">
          <BellRing className="w-3.5 h-3.5" /> {last ? "Relancer le client" : "Notifier le client"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="notify-client-modal">
          <DialogHeader>
            <DialogTitle>Notifier le client — publications en attente de validation</DialogTitle>
          </DialogHeader>
          {!data ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm flex flex-wrap justify-between gap-2" data-testid="notify-recipient">
                <span>À : <b>{data.client_name}</b>{data.to ? ` — ${data.to}` : ""}</span>
                <span className="text-orange-700 font-medium">{data.pending_count} publication(s) en attente</span>
              </div>
              {!data.to && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="notify-no-email">
                  Aucune adresse e-mail de contact n'est renseignée pour ce client — ajoutez-la dans sa fiche (Informations).
                </p>
              )}
              {data.publications?.length > 0 && (
                <div className="text-xs text-neutral-500 border border-neutral-100 rounded-lg p-3 space-y-1" data-testid="notify-pending-list">
                  <p className="font-semibold text-neutral-600 mb-1">Publications concernées :</p>
                  {data.publications.slice(0, 6).map((p) => (
                    <p key={p.id}>• {new Date(`${p.date}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}{p.time ? ` à ${p.time}` : ""} — {p.title}</p>
                  ))}
                  {data.publications.length > 6 && <p>… et {data.publications.length - 6} autre(s)</p>}
                </div>
              )}
              <div>
                <Label className="text-xs">Objet</Label>
                <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="notify-subject" />
              </div>
              <div>
                <Label className="text-xs">Message (modifiable avant envoi)</Label>
                <Textarea rows={11} className="mt-1 text-sm" value={body} onChange={(e) => setBody(e.target.value)} data-testid="notify-body" />
              </div>
              <p className="text-xs text-neutral-400">
                Le bouton « Valider mes publications » (lien sécurisé vers l'espace client) sera ajouté automatiquement : {data.portal_url}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="notify-cancel-btn">Annuler</Button>
                <Button onClick={send} disabled={busy || !data.to || !body.trim()}
                  className="bg-orange-600 hover:bg-orange-700 text-white gap-1.5" data-testid="notify-send-btn">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Envoyer l'e-mail
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
